import { App, Select, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { MobileTopLeaderProjectProgressCard } from '@/components/mobile/MobileTopLeaderProjectProgressCard';
import { PROJECT_STATUS } from '@/constants/project';
import { ROUTES } from '@/constants/routes';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import * as projectsApi from '@/services/api/projects';
import type { ProjectListItem, ProjectMembersResult } from '@/types/project';
import type { ProjectReportRecord, QueryProjectReportsParams } from '@/types/project-report';

const ALL_PROJECTS = '__all__';
const ALL_PEOPLE = '__all__';

interface MemberOption {
  value: string;
  label: string;
}

function buildMemberOptions(
  membersResult: ProjectMembersResult | null,
  project: ProjectListItem | null,
): MemberOption[] {
  if (!membersResult) {
    return [];
  }

  const managerId = membersResult.projectManagerId;
  const options: MemberOption[] = [{ value: ALL_PEOPLE, label: '全部人员' }];
  const seen = new Set<string>([ALL_PEOPLE]);

  const manager = project?.projectManager;
  if (manager) {
    options.push({
      value: manager.id,
      label: `${manager.name}（项目负责人）`,
    });
    seen.add(manager.id);
  }

  for (const item of membersResult.items) {
    if (seen.has(item.user.id)) {
      continue;
    }
    const roleLabel = item.user.id === managerId ? '项目负责人' : '项目成员';
    options.push({
      value: item.user.id,
      label: `${item.user.name}（${roleLabel}）`,
    });
    seen.add(item.user.id);
  }

  return options;
}

export function TopLeaderProjectOversightPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();

  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>(ALL_PROJECTS);

  const [membersLoading, setMembersLoading] = useState(false);
  const [membersResult, setMembersResult] = useState<ProjectMembersResult | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>(ALL_PEOPLE);

  const [reportsLoading, setReportsLoading] = useState(false);
  const [reports, setReports] = useState<ProjectReportRecord[]>([]);

  const selectedProject = useMemo(() => {
    if (selectedProjectId === ALL_PROJECTS) {
      return null;
    }
    return projects.find((project) => project.id === selectedProjectId) ?? null;
  }, [projects, selectedProjectId]);

  const memberOptions = useMemo(() => {
    if (selectedProjectId === ALL_PROJECTS) {
      return [{ value: ALL_PEOPLE, label: '全部人员' }];
    }
    return buildMemberOptions(membersResult, selectedProject);
  }, [membersResult, selectedProject, selectedProjectId]);

  const projectSelectOptions = useMemo(
    () => [
      { value: ALL_PROJECTS, label: '全部项目' },
      ...projects.map((project) => ({ value: project.id, label: project.name })),
    ],
    [projects],
  );

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const result = await projectsApi.fetchProjects({
        page: 1,
        pageSize: 100,
        status: PROJECT_STATUS.ACTIVE,
      });
      setProjects(result.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目列表失败';
      message.error(text);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, [message]);

  const loadMembers = useCallback(
    async (projectId: string) => {
      setMembersLoading(true);
      try {
        const result = await projectsApi.fetchProjectMembers(projectId);
        setMembersResult(result);
      } catch (error) {
        const text = error instanceof ApiError ? error.message : '加载项目成员失败';
        message.error(text);
        setMembersResult(null);
      } finally {
        setMembersLoading(false);
      }
    },
    [message],
  );

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const params: QueryProjectReportsParams = {
        page: 1,
        pageSize: 100,
      };
      if (selectedProjectId !== ALL_PROJECTS) {
        params.projectId = selectedProjectId;
      }
      if (selectedUserId !== ALL_PEOPLE) {
        params.createdById = selectedUserId;
      }
      const result = await projectReportsApi.fetchProjectReports(params);
      setReports(result.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目进展失败';
      message.error(text);
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, [message, selectedProjectId, selectedUserId]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (selectedProjectId === ALL_PROJECTS) {
      setMembersResult(null);
      setSelectedUserId(ALL_PEOPLE);
      return;
    }
    void loadMembers(selectedProjectId);
  }, [loadMembers, selectedProjectId]);

  useEffect(() => {
    if (projectsLoading) {
      return;
    }
    void loadReports();
  }, [loadReports, projectsLoading]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    setSelectedUserId(ALL_PEOPLE);
  };

  if (projectsLoading) {
    return (
      <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div className="mobile-workbench">
      <div className="mobile-filter-panel">
        <div className="mobile-filter-row">
          <span className="mobile-filter-label">项目</span>
          <Select
            style={{ flex: 1 }}
            value={selectedProjectId}
            onChange={handleProjectChange}
            options={projectSelectOptions}
          />
        </div>
        <div className="mobile-filter-row">
          <span className="mobile-filter-label">人员</span>
          <Select
            style={{ flex: 1 }}
            loading={membersLoading}
            disabled={selectedProjectId === ALL_PROJECTS}
            value={selectedUserId}
            onChange={setSelectedUserId}
            options={memberOptions}
          />
        </div>
      </div>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">项目进展列表</span>
          <span className="mobile-section__meta">{reportsLoading ? '…' : `${reports.length} 条`}</span>
        </div>

        {reportsLoading ? (
          <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : reports.length === 0 ? (
          <MobileEmpty message="暂无项目进展" />
        ) : (
          reports.map((report) => (
            <MobileTopLeaderProjectProgressCard
              key={report.id}
              report={report}
              onClick={() =>
                navigate(
                  ROUTES.mobile.leader.projectReportDetail(report.project.id, report.id),
                )
              }
            />
          ))
        )}
      </section>
    </div>
  );
}
